import { userState } from './auth.svelte';
import { impersonationState } from './rolePermissions.svelte';
import type { AdminAccessEntry } from '$lib/types';

/**
 * The user whose VIEW the app should render: the impersonated staff member
 * when impersonation is active, otherwise the signed-in user.
 *
 * Model note (bug 2026-07-31): impersonation originally swapped only the
 * rolePermissions tier, so scope consumers (grade tiles, admin queries)
 * still saw the real user. Anything that scopes what is DISPLAYED must read
 * from here, not from userState. userState remains the source of truth for
 * who is actually authenticated (Firestore rules always apply to them).
 */

function impersonatedAdminEntry(): AdminAccessEntry | undefined {
	const email = impersonationState.staff?.email?.trim().toLowerCase();
	if (!email) return undefined;
	return userState.adminLookup?.[email];
}

export const effectiveUser = {
	get isImpersonating(): boolean {
		return impersonationState.staff !== null;
	},
	/** The impersonated staff record, or null when not impersonating. */
	get staff() {
		return impersonationState.staff;
	},
	/** Admin role of whoever we're viewing as (undefined = no admin grant). */
	get adminRole(): AdminAccessEntry['role'] | undefined {
		return this.isImpersonating ? impersonatedAdminEntry()?.role : userState.adminRole;
	},
	get isAdmin(): boolean {
		return this.isImpersonating ? impersonatedAdminEntry() !== undefined : !!userState.isAdmin;
	},
	/** School scope: the impersonated person's admin grant school, falling back
	 *  to their staff record's school (relevant when they have no grant).
	 *
	 *  Normalized, because this value does not only *describe* the grant — it
	 *  narrows Firestore queries (`getStudentDocsInAdminScope`,
	 *  `getSectionsForAdminSchool`). Two spellings of "no school" got through
	 *  and silently shrank the app:
	 *
	 *  - A `super` grant carrying a leftover `school_id`. `lookups/admins` is
	 *    merged into by two writers (admins.csv and the grants UI) and neither
	 *    clears a field the other omits, so a super promoted in the UI kept the
	 *    school its old CSV row named. The rules let them read the district;
	 *    the client asked only for one school, so /admin/analyze showed one
	 *    school with no error to explain it.
	 *  - `school_id: ""`, which is what the grant UI writes when no school is
	 *    chosen. Falsy here, so it read as district-wide, while the rules read
	 *    it as a school — see `grantSchoolId()` in firestore.rules, now fixed
	 *    to agree with this.
	 */
	get schoolId(): string | undefined {
		if (this.adminRole === 'super') return undefined;
		const raw = this.isImpersonating
			? (impersonatedAdminEntry()?.school_id ?? impersonationState.staff?.school_id)
			: userState.schoolId;
		return raw?.trim() ? raw.trim() : undefined;
	},
	get adminScope(): unknown {
		return this.isImpersonating ? impersonatedAdminEntry()?.scope : userState.adminScope;
	},
	/**
	 * Does this grant reach every student? Mirrors `adminReachesAllStudents()`
	 * in firestore.rules, which is what gates an unfiltered collection-group
	 * sweep — a scoped or school-bound admin asking for *all* plans is exactly
	 * the read the rules now refuse, so screens built on that sweep have to
	 * check the same condition or they surface a permission error instead of an
	 * explanation.
	 */
	get reachesAllStudents(): boolean {
		const role = this.adminRole;
		// `== null` rather than falsy, to match the rule exactly: the rules test
		// `entry.get("school_id", null) == null`, so an empty-string school_id is
		// school-bound *there* while a falsy check would read it as district-wide
		// here — and the divergence shows up as a screen that renders and then
		// 403s, which is the worst way to find out.
		return role === 'super' || (role === 'admin' && this.schoolId == null);
	}
};
